import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { useAuth } from '../contexts/AuthContext';
import TelegramLoginButton from '../components/TelegramLoginButton';
import axios from 'axios';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [botUsername, setBotUsername] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Handle Telegram authentication callback
  const handleTelegramAuth = useCallback(async (user: any) => {
    console.log('🔐 Processing Telegram auth for user:', user);
    setError('');
    setLoading(true);

    try {
      // Send Telegram auth data to backend for verification
      const response = await axios.post('/api/v1/auth/telegram', {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        username: user.username,
        photo_url: user.photo_url,
        auth_date: user.auth_date,
        hash: user.hash,
      });

      console.log('✅ Backend authentication successful');

      // Login successful - store token and user info
      const { access_token, user_info } = response.data;
      login(access_token, user_info);

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      console.error('❌ Authentication error:', err);

      if (err.response?.status === 403) {
        setError('Access Denied: You are not authorized to access this dashboard. Please contact an administrator.');
      } else if (err.response?.status === 401) {
        setError('Authentication failed: Invalid credentials. Please try again.');
      } else {
        setError('An error occurred during authentication. Please try again.');
      }

      setLoading(false);
    }
  }, [login, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Fetch bot configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get('/api/v1/auth/config');
        setBotUsername(response.data.bot_username);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch auth config:', err);
        setError('Failed to load authentication configuration');
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  if (loading && !error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Voice Summary Dashboard
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Administrator Access Only
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {botUsername && !loading ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <TelegramLoginButton
                botName={botUsername}
                onAuth={handleTelegramAuth}
                buttonSize="large"
                requestAccess={true}
              />
            </Box>

            <Accordion sx={{ mt: 2, textAlign: 'left' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="login-instructions-content"
                id="login-instructions-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={500}>
                    Проблемы со входом? Читайте здесь
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  <strong>Как работает Telegram Login:</strong>
                </Typography>

                <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                  <strong>Вариант 1 (быстрый):</strong>
                  <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>Откройте <Link href="https://web.telegram.org" target="_blank" rel="noopener">web.telegram.org</Link> в этом же браузере</li>
                    <li>Авторизуйтесь там через Telegram</li>
                    <li>Вернитесь на эту страницу и обновите</li>
                    <li>Кнопка покажет "Login as @ваш_username"</li>
                  </ol>
                </Typography>

                <Typography variant="body2" component="div" sx={{ mb: 2 }}>
                  <strong>Вариант 2 (если кнопки "Login as" нет):</strong>
                  <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>Нажмите кнопку "Log in with Telegram"</li>
                    <li>Введите номер телефона</li>
                    <li><strong>⚠️ Код придет в приложение Telegram</strong> (НЕ по SMS!)</li>
                    <li>Откройте Telegram на телефоне/компьютере</li>
                    <li>Скопируйте код и введите его в форму</li>
                  </ol>
                </Typography>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Важно:</strong> Коды авторизации Telegram приходят ТОЛЬКО в приложение Telegram,
                    а не по SMS. Убедитесь, что у вас установлен Telegram на телефоне или компьютере.
                  </Typography>
                </Alert>

                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Для localhost:</strong> Если вы открываете дашборд с localhost,
                    рекомендуется использовать Вариант 1 (авторизация через web.telegram.org).
                  </Typography>
                </Alert>
              </AccordionDetails>
            </Accordion>
          </>
        ) : (
          <Alert severity="warning">
            Unable to load authentication. Please refresh the page.
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
          Only authorized administrators can access this dashboard.
          If you believe you should have access, contact the system administrator.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
